import AsyncSelect from "react-select/async";
import type { AsyncProps } from "react-select/async";
import type { GroupBase, MultiValue, OptionsOrGroups, SelectInstance } from "react-select";
import { useCallback, useRef, useState } from "react";

import useSelectViewport from "./useSelectViewport";

import { myAxios } from "src/components/common/functions";
import { json } from "./types";

export type SelectOption = {
    label: string;
    value: string;
};

type AsyncFormSelectOptionMapper = (item: any) => SelectOption & Record<string, any>;

export function AsyncFormSelect({
	isMulti = false, 
	placeholder = "", 
	required = false, 
	inputId,
	name,
	fileName, 
	value,
	onChange, 
    className, 
	readOnly = false, 
	formValues = {},
    optionMapper,
    fitMobileViewport = false
}: {
	isMulti?: boolean;
	placeholder?: string;
	required?: boolean;
	name?: string;
	inputId?: string;
	fileName: string;
	value: MultiValue<SelectOption> | SelectOption | null;
	onChange: (next: SelectOption | MultiValue<SelectOption> | null) => void;
    className?: string;
	readOnly?: boolean;
	formValues?: json;
    optionMapper?: AsyncFormSelectOptionMapper;
    fitMobileViewport?: boolean;
}) {
    const selectRef = useRef<SelectInstance<SelectOption, boolean>>(null);
    const [menuOpen, setMenuOpen] = useState(false);
    const viewportMenu = useSelectViewport(selectRef, menuOpen, fitMobileViewport);
	const typingTimeoutRef = useRef<number | null>(null);
    const isSelectOption = (item: unknown): item is SelectOption =>
        item !== null &&
        typeof item === "object" &&
        "value" in item &&
        "label" in item &&
        typeof (item as SelectOption).value === "string" &&
        typeof (item as SelectOption).label === "string";
    const normalizedValue = isMulti
        ? Array.isArray(value)
            ? value.filter(isSelectOption)
            : []
        : !Array.isArray(value) && isSelectOption(value)
            ? value
            : null;
	const loadOptions: AsyncProps<SelectOption, true, GroupBase<SelectOption>>["loadOptions"] = useCallback(
		(inputValue: string, callback: (options: OptionsOrGroups<SelectOption, GroupBase<SelectOption>>) => void) => {
			if (typingTimeoutRef.current) {
				window.clearTimeout(typingTimeoutRef.current);
			}
			const q = inputValue.trim();
			if (!q) {
				callback([]);
				return;
			}
			typingTimeoutRef.current = window.setTimeout(async () => {
				const formData = new FormData();
				formData.append("q", q);
				Object.entries(formValues).forEach(([key, value]) => formData.append(key, String(value ?? "")));
				const res = await myAxios({ fileName, formData });
				if (!res) {
					callback([]);
					return;
				}
				callback(res.data.map((item: any) => 
                    optionMapper?
                    optionMapper(item)
                    :
                    ({ value: String(item.value), label: item.label, ...(item as object)})
                ));
			}, 500);
		},
		[fileName, formValues, optionMapper]
	);
	return (
		<>
			<AsyncSelect
                ref={selectRef}
                onMenuOpen={() => setMenuOpen(true)}
                onMenuClose={() => setMenuOpen(false)}
                menuPlacement={fitMobileViewport ? viewportMenu.placement : "bottom"}
                maxMenuHeight={fitMobileViewport ? viewportMenu.maxHeight : 300}
                minMenuHeight={fitMobileViewport ? Math.min(100, viewportMenu.maxHeight) : 140}
                menuShouldScrollIntoView={fitMobileViewport ? false : undefined}
				isDisabled = {readOnly}
                className = {className}
				inputId = {inputId}
				isMulti = {isMulti}
				required = {required}
				menuPortalTarget = {fitMobileViewport && viewportMenu.mobile ? null : typeof document !== "undefined" ? document.body : null}
				menuPosition = {fitMobileViewport && viewportMenu.mobile ? "absolute" : "fixed"}
				cacheOptions
				defaultOptions = {false}
				loadOptions = {loadOptions}
				isClearable
				placeholder = {placeholder}
				value = {normalizedValue}
				onChange = {opts => 
					isMulti?
					onChange(opts as MultiValue<SelectOption> ?? null):
					onChange(opts as SelectOption ?? null)
				}
				styles = {{
					container: base => ({
						...base,
						minWidth: fitMobileViewport ? 0 : 260,
                        ...(fitMobileViewport && viewportMenu.mobile && menuOpen ? {zIndex: 9999} : {})
					}),
					menuPortal: base => ({
						...base,
						zIndex: 9999,
					}),
				}}
			/>
			{/* Hidden inputs for FormData (one per record) */}
			{isMulti? 
			(name && normalizedValue && (normalizedValue as MultiValue<SelectOption>).map(v => (
				<input
					key = {v.value}
					type = "hidden"
					name = {name}
					value = {(v as SelectOption).value ?? ""}
				/>
			)))
			:
			name && normalizedValue && 
				<input
					type = "hidden"
					name = {name}
					value = {(normalizedValue as SelectOption).value ?? ""}
				/>
			}
		</>
	);
}
